import { forwardRef, useState } from "react";
import { Button, Input, Modal, Progress, Space } from "antd";
import { PauseCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { message } from "../message";
import { fetchEventData } from "fetch-sse";

import { trace, useClickTrace } from "../behavior";
import { HtmlParser } from "./HtmlParser";
import { sendMessage } from "../Context";
import { store } from "../Store";
import { getClientId, getToken } from "../api";

const txts = ["口算练习题", "二年级数学练习题", "阅读理解题", "英语单词测试题"];

let ctrl: AbortController | null = null;
const win = global as any;

/**
 * 创建一个永远不会达到100%的非线性进度条函数
 * @param options 配置选项
 * @returns 返回当前进度的函数
 */
function createInfiniteProgressTracker(
  options: {
    initialProgress?: number; // 初始进度（可选，默认0）
    incrementBase?: number; // 基础增量（可选，默认1.0）
    slowdownFactor?: number; // 减速因子（可选，默认3.0）
    cap?: number; // 进度上限（可选，默认99.99%）
  } = {}
): () => number {
  const {
    initialProgress = 0,
    incrementBase = 1.0,
    slowdownFactor = 3.0,
    cap = 99.99,
  } = options;

  let currentProgress = Math.min(initialProgress, cap - 0.01); // 确保初始值低于上限

  /**
   * 获取并更新当前进度
   * @returns 当前进度值（0 - cap）
   */
  return function getProgress(): number {
    // 计算非线性增量：越接近上限，增量越小
    const normalizedProgress = currentProgress / cap;
    const slowdownEffect = 1 + normalizedProgress * slowdownFactor;
    const increment =
      (incrementBase / slowdownEffect) * (1 - normalizedProgress);

    // 增加进度（确保不超过上限）
    currentProgress = Math.min(currentProgress + increment, cap);

    return currentProgress;
  };
}

// 使用示例
const getProgress = createInfiniteProgressTracker({
  incrementBase: 2.0, // 初始增长较快
  slowdownFactor: 5.0, // 减速效果明显
  cap: 99.9, // 进度上限设为99.9%
});

const CreateNewButton = forwardRef(
  (props: { onLogin: () => void }, ref: any) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [loading, setLoading] = useState(false);
    const [percent, setPercent] = useState(100);
    // const user = useLiveQuery(() => store.getUser());

    const showModal = useClickTrace("新建试卷", () => {
      // if (!user) {
      //   message.warning("请登录");
      //   props.onLogin();
      //   return;
      // }
      if (!win.pageId) {
        message.warning("请先选择一个页面");
        return;
      }
      setIsModalOpen(true);
    });

    const handleOk = async () => {
      if (!inputValue) {
        message.error("请输入试卷的内容");
        return;
      }
      setLoading(true);
      let showed = false;
      function showMessage() {
        if (showed) return;
        showed = true;
        message.open({
          key: "create-exam-progress",
          type: "loading",
          content: (
            <span>
              正在生成试卷，请稍后..{" "}
              <Button
                type="text"
                size="small"
                icon={<PauseCircleOutlined />}
                style={{ marginLeft: 20 }}
                onClick={handleCancel}
              >
                停止
              </Button>
            </span>
          ),
          duration: 0,
        });
      }
      try {
        let err;
        const htmlParser = new HtmlParser({
          onTagComplete: (result) => {
            if (result.level === 0) {
              process.env.NODE_ENV === "development" &&
                console.log("解析HTML片段：\n", result.completeHtml);
              sendMessage(win.pageId, result.completeHtml, "after");
            }
          },
          onParseEnd: () => {
            // console.log("\n✅ 解析完成：所有标签已处理");
          },
          onParseError: (err) => {
            console.error("❌ 解析错误：", err);
          },
        });
        ctrl = new AbortController();
        trace("click", { name: "生成", value: inputValue });
        await fetchEventData(
          `${process.env.AI_SERVER_PATH}/api/agent/exec/exampaper`,
          {
            method: "POST",
            signal: ctrl?.signal,
            headers: {
              Authorization: getToken() ? `Bearer ${getToken()}` : "",
              "X-Client-Id": getClientId(),
              "Content-Type": "application/json",
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
              // 添加一个唯一的自定义头部，包含当前时间戳
              "X-Custom-No-Cache": new Date().getTime().toString(),
            },
            data: {
              question: "生成" + inputValue,
              tagEnabled: false,
            },
            onError(error) {
              err = error;
            },
            async onMessage(ev) {
              const data = JSON.parse(ev!.data);
              // console.log(ev?.event, data);
              if (ev?.event === "MESSAGE") {
                setIsModalOpen(false);
                setPercent(getProgress());
                showMessage();
                htmlParser.addHtml(data.content);
              }
              if (ev?.event === "ERROR") {
                err = data.message;
              }
            },
          }
        );
        htmlParser.finish();
        if (err) {
          throw new Error(err);
        }
        setPercent(100);
        message.destroy("create-exam-progress");
        message.success("试卷内容创建成功");
        setLoading(false);
        store.setOption(
          "useCount",
          ((await store.getOptions())?.useCount || 0) + 1
        );
      } catch (e: any) {
        setLoading(false);
        message.destroy("create-exam-progress");
        if (e.message.includes("AbortError")) {
          setPercent(100);
          message.info("试卷内容创建已取消");
          return;
        }
        message.error(e.message);
        trace("error", { name: "生成失败", value: e.message });
      }
    };

    const handleCancel = () => {
      ctrl?.abort();
      setIsModalOpen(false);
    };

    return (
      <>
        {percent !== 100 && (
          <Progress
            percent={percent}
            showInfo={false}
            size="small"
            strokeColor="#1677ff"
          />
        )}
        {percent !== 100 ? (
          <Button
            type="text"
            className="cancel-button"
            size="small"
            danger
            onClick={handleCancel}
          >
            取消
          </Button>
        ) : (
          <Button
            ref={ref}
            type="primary"
            shape="circle"
            size="small"
            className="ep-create-btn"
            icon={<PlusOutlined />}
            title="新建试题"
            {...showModal}
          ></Button>
        )}
        <Modal
          title="新建试题"
          closable={{ "aria-label": "Custom Close Button" }}
          open={isModalOpen}
          onOk={handleOk}
          onCancel={handleCancel}
          okButtonProps={{ loading }}
          okText="创建"
          cancelText="取消"
        >
          <div style={{ margin: "5px 0" }}>
            输入要生成的试题要求：
            <Space.Compact size="small" className="create-new-button-space">
              {txts.map((txt, i) => (
                <Button key={i} onClick={() => setInputValue(txt)}>
                  {txt}
                </Button>
              ))}
            </Space.Compact>
          </div>
          <Input.TextArea
            value={inputValue}
            allowClear
            onChange={(e) => setInputValue(e.target.value)}
            autoSize={{ minRows: 3, maxRows: 6 }}
            placeholder="请输入试题范围，例如：小学二年级数学混合运算练习题"
          ></Input.TextArea>
        </Modal>
      </>
    );
  }
);

export default CreateNewButton;
